import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "services/api";
import { __ } from "services/Translator";
import AuthGate from "components/auth/AuthGate";
import RaceBanner from "components/weeklyRace/RaceBanner";
import WeeklyRaceContent from "components/weeklyRace/WeeklyRaceContent";
import TournamentsTeaser from "components/weeklyRace/TournamentsTeaser";
import { isWeeklyRaceMockId } from "components/weeklyRace/weeklyRaceMockData";

const LEADERBOARD_POLL_MS = 20000;
const LEADERBOARD_LIMIT = 50;

// Shimmer bar used by the loading skeleton. Reuses the global `shimmer`
// keyframe (_weeklyRace.scss); reduced-motion is disabled there via the
// `.weekly-race *` rule, so the skeleton respects prefers-reduced-motion.
const SHIMMER = {
  background:
    "linear-gradient(90deg, rgba(255,255,255,.05) 25%, rgba(255,255,255,.11) 37%, rgba(255,255,255,.05) 63%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.4s linear infinite",
  borderRadius: 6,
};

const bar = (style) => (
  <span style={{ display: "block", ...SHIMMER, ...style }} aria-hidden="true" />
);

function TournamentsSkeleton() {
  return (
    <div
      className="weekly-race weekly-race--loading"
      aria-busy="true"
      aria-live="polite"
      aria-label={__("Loading tournaments...")}
    >
      <div className="weekly-race__page">
        <section className="panel bg-hero race-hero" aria-hidden="true">
          <div className="race-copy" style={{ width: "100%", maxWidth: "100%" }}>
            {bar({ width: 120, height: 22, borderRadius: 999, marginBottom: 14 })}
            {bar({ width: "70%", height: 46, marginBottom: 10 })}
            {bar({ width: "50%", height: 30, marginBottom: 18 })}
            {bar({ width: "80%", height: 14, marginBottom: 8 })}
            {bar({ width: "55%", height: 14 })}
          </div>
        </section>

        <div className="cd-wrap">
          {bar({ minHeight: 160, borderRadius: 12 })}
          {bar({ minHeight: 160, borderRadius: 12 })}
        </div>

        <section
          className="panel bg-leader lb-card"
          aria-hidden="true"
          style={{ marginTop: 18 }}
        >
          <div className="lb-head">
            <span>{__("Rank")}</span>
            <span>{__("Player")}</span>
            <span style={{ textAlign: "right" }}>{__("Prize")}</span>
            <span style={{ textAlign: "right" }}>{__("Points")}</span>
          </div>
          {Array.from({ length: 10 }).map((_, index) => (
            <div className="lb-row" key={index}>
              <span className="rk-cell">
                {bar({ width: 26, height: 26, borderRadius: "50%" })}
              </span>
              <span className="pl">
                {bar({ width: 34, height: 34, borderRadius: "50%" })}
                {bar({ width: 120, height: 12 })}
              </span>
              <span className="pz">
                {bar({ display: "inline-block", width: 60, height: 14 })}
              </span>
              <span className="mt">
                {bar({ display: "inline-block", width: 50, height: 14 })}
              </span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

const STATE_TITLE = {
  color: "var(--t-hi)",
  fontFamily: "var(--f-display)",
  fontWeight: 400,
  fontSize: 34,
  letterSpacing: 1,
  textTransform: "uppercase",
  margin: "0 0 10px",
};
const STATE_MSG = {
  color: "var(--t-muted)",
  fontSize: 14,
  lineHeight: 1.6,
  maxWidth: 460,
  margin: "0 auto 22px",
};

// Authenticated race dashboard. Mounted ONLY when the visitor is logged in
// (AuthGate never renders children for a guest), so the /race/* calls — and
// the personal "your rank" logic — never fire for a stranger. Logged-out
// visitors get <TournamentsTeaser/> instead.
function TournamentsDashboard(props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [primaryRace, setPrimaryRace] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [qualifyingGames, setQualifyingGames] = useState([]);
  const [otherRaces, setOtherRaces] = useState([]);
  const [pastRaces, setPastRaces] = useState([]);

  const fetchLeaderboard = useCallback(async (raceId) => {
    if (!raceId || isWeeklyRaceMockId(raceId)) return;
    const response = await api.get(
      `/race/leaderboard/${raceId}/${LEADERBOARD_LIMIT}`
    );
    if (response.status === 200 && response.data?.length) {
      setLeaderboard(response.data);
    }
  }, []);

  const loadRaces = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [activeRes, inactiveRes] = await Promise.all([
        api.get("/race/list/active"),
        api.get("/race/list/inactive", { page: 1, per_page: 5 }),
      ]);

      // api.js never rejects — a transport/server failure surfaces as a
      // non-200 status, not a throw. Treat a failed active-list call as a
      // genuine error (honest retry state), never as "no active race".
      if (activeRes.status !== 200) {
        setError(true);
        return;
      }

      const activeList = Array.isArray(activeRes.data)
        ? activeRes.data
        : activeRes.data?.races || [];

      const main = activeList[0] || null;
      let leaderboardData = [];
      let gamesData = [];
      const otherData = activeList.slice(1);
      let pastData = [];

      if (inactiveRes.status === 200) {
        const ended = Array.isArray(inactiveRes.data)
          ? inactiveRes.data
          : inactiveRes.data?.races || [];
        pastData = ended.slice(0, 5);
      }

      if (main?.id && !isWeeklyRaceMockId(main.id)) {
        const [lbRes, gamesRes] = await Promise.all([
          api.get(`/race/leaderboard/${main.id}/${LEADERBOARD_LIMIT}`),
          api.get(`/race/games/${main.id}/1`),
        ]);
        if (lbRes.status === 200) {
          leaderboardData = lbRes.data || [];
        }
        if (gamesRes.status === 200) {
          gamesData = gamesRes.data?.games || [];
        }
      }

      setPrimaryRace(main);
      setLeaderboard(leaderboardData);
      setQualifyingGames(gamesData);
      setOtherRaces(otherData);
      setPastRaces(pastData);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRaces();
  }, [loadRaces]);

  useEffect(() => {
    if (!primaryRace?.id || isWeeklyRaceMockId(primaryRace.id)) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      fetchLeaderboard(primaryRace.id);
    }, LEADERBOARD_POLL_MS);
    return () => window.clearInterval(timer);
  }, [primaryRace?.id, fetchLeaderboard]);

  if (loading) {
    return <TournamentsSkeleton />;
  }

  if (error) {
    return (
      <div className="weekly-race weekly-race--error">
        <div className="weekly-race__page weekly-race__state">
          <section
            className="panel bg-leader"
            role="alert"
            style={{ padding: 44, textAlign: "center" }}
          >
            <h2 style={STATE_TITLE}>
              {__("We couldn't load the tournaments")}
            </h2>
            <p style={STATE_MSG}>
              {__(
                "Something went wrong reaching the race server. Please try again."
              )}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={loadRaces}
            >
              {__("Try again")}
            </button>
          </section>
        </div>
      </div>
    );
  }

  if (!primaryRace) {
    return (
      <div className="weekly-race weekly-race--empty">
        <div className="weekly-race__page weekly-race__state">
          <section
            className="panel bg-leader"
            style={{ padding: 44, textAlign: "center" }}
          >
            <span className="eyebrow" style={{ marginBottom: 14 }}>
              <span className="dot-live" aria-hidden="true" />
              {__("Tournaments")}
            </span>
            <h2 style={STATE_TITLE}>{__("No active race right now")}</h2>
            <p style={STATE_MSG}>
              {__(
                "The next tournament is on its way. Check back soon, or explore our latest promotions in the meantime."
              )}
            </p>
            <Link to="/promotions" className="btn btn-primary btn-lg">
              {__("See promotions")}
            </Link>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl">
      <RaceBanner />
      <WeeklyRaceContent
        race={primaryRace}
        leaderboard={leaderboard}
        qualifyingGames={qualifyingGames}
        otherRaces={otherRaces}
        pastRaces={pastRaces}
        userDetails={props.userDetails}
        isAuthenticated={props.isAuthenticated}
      />
    </div>
  );
}

export default function Tournaments(props) {
  return (
    <AuthGate teaser={<TournamentsTeaser {...props} />}>
      <TournamentsDashboard {...props} />
    </AuthGate>
  );
}
